(ns ycweb.app.website.cart.pages.cart-items
  (:require
   [rum.core :as rum]
   [util.string :as str]
   [ycweb.app.website.cart.model :as model]
   [ycweb.app.website.cart.parts.common :as cart.common]
   [ycweb.app.website.cart.parts.discounts :as cart.discounts]
   [ycweb.app.website.common :as web-common]
   [ycweb.components.common :as cmn]
   [ycweb.components.feedback :as feedback]
   [ycweb.components.grid :as grid]
   [ycweb.util.core :as u]
   [ycweb.util.urlstate :as urlstate]))

(defn can-increase-qty? [item variation]
  (cond
    (model/free-special-item? item) false

    (:registry_available_qty item)
    (< (:qty item) (:registry_available_qty item))

    ;; Already blocks checkout (Awaiting stock / Not available / TBA price)
    (:checkout_blocked variation) false

    ;; Discontinued: adding past what's left greys out checkout
    (= 2 (:notavailable variation))
    (< (:qty item) (or (:sellablestock variation) 0))

    (pos? (or (:limitedstock_max_per_order variation) 0))
    (< (:qty item) (:limitedstock_max_per_order variation))

    (:cover_voucher item) false

    :else true))

(defn can-reduce-qty? [item variation]
  (cond
    (model/free-special-item? item) false

    (zero? (:qty item)) false

    (:cover_voucher item) false

    :else true))

(defn can-remove? [item variation]
  (cond
    (get-in item [:special :added]) false
    (get-in item [:qtyfixed]) false
    (:system_item item) false

    :else true))

(rum/defc cart-item-hero [app-atom cart-summary-atom item variation covers]
  (let [cover-voucher (:cover_voucher item)
        specials (cmn/curact cart-summary-atom [:specials])
        cover-details (when cover-voucher (get covers (:cover_refid cover-voucher)))
        special (get-in specials [(get-in item [:special :refid])])
        ratio-special? (pos? (or (:ratio_qty special) 0))
        ratio-special-ratio-qty (or (:ratio_qty special) 0)
        ratio-special-free-qty (or (:cheapest_free special) 0)

        special-sticker
        (when
          (and ratio-special? (pos? ratio-special-ratio-qty) (pos? ratio-special-free-qty))
          [:div.cart-item__graphic-sticker
           [:div.deal-ratio-icon
            [:span.deal-ratio-icon__int ratio-special-ratio-qty]
            [:span.deal-ratio-icon__for "for"]
            [:span.deal-ratio-icon__int (- ratio-special-ratio-qty ratio-special-free-qty)]]])]

    [:figure {:class "cart-item__hero"}
     [:a {:href (:page_url variation) :class "block-link"}
      (cond
        ;; Cover voucher image
        (and cover-voucher (not (:forregistry_uuid item)))

        (web-common/cloudinary-lazy-image app-atom
          {:public_id (get-in cover-details [:image :public_id])
           :width 256
           ;; Cover vouchers are not square, so just specify width
           :alt (:title cover-details "Gift Voucher")
           :html-class "yc-simple-image yc-simple-image--full cart-item__image"
           :lazyload true})

        ;; Regular product image
        (:public_id (:image variation))
        (web-common/cloudinary-lazy-image app-atom
          {:public_id (get-in variation [:image :public_id])
           :width 256
           :height 256
           :alt (:title variation)
           :html-class "yc-simple-image yc-simple-image--full cart-item__image"
           :lazyload true})

        ;; Color block for items with only color info
        (:colour variation)
        (let [colorpic (-> (:colour variation)
                         (str/replace "(" "")
                         (str/replace " " "_")
                         (str/lower-case))]
          [:div {:class "cart-item__color-block"}
           [:img {:src (str "/images/colours/" colorpic ".gif")
                  :class "colourBlock"
                  :width 64
                  :height 64}]])

        ;; Fallback - no image
        :else
        [:div {:class "cart-item__no-image"}
         [:div {:class "cart-item__no-image-placeholder"}
          "No image"]])
      special-sticker]]))

(defn- build-product-description [variation]
  (let [feature (u/presence (:feature variation))
        display_packsize (:display_packsize variation)
        selling_packsize (:selling_packsize variation)
        packdescription (:packdescription variation)
        parts []

        parts
        (if feature (conj parts feature) parts)

        parts
        (if (and display_packsize (> display_packsize 1) (= selling_packsize 1))
          (conj parts (str packdescription " of " display_packsize))
          parts)]
    (interpose [:span {:class "pipe-divider"} " | "] parts)))

(rum/defc cart-item-description [app-atom cart-atom item variation checkout-issue]
  (let [cover-voucher (:cover_voucher item)
        special (:special item)]
    [:div {:class "cart-item__description"}
     ;; Title
     [:h2 {:class "cart-item__title"}
      [:a {:href (:page_url variation)}
       [:span (:title variation)]
       (when (:forregistry_uuid item) " - For Gift Registry")]]

     ;; Meta information (variant details and product code)
     [:small {:class "cart-item__meta"}
      (let [description-parts (build-product-description variation)]
        (when (seq description-parts)
          [:<> description-parts " | "]))
      (str "Product code: " (:code variation))]

     ;; Unit price
     [:p {:class "cart-item__unit-price"}
      [:b "R" (u/currency-format (:price item))] " each"]

     ;; Stock status — suppressed for free special items and registry purchases
     (when-not (or (model/free-special-item? item) (:forregistry_uuid item))
       [:p {:class "cart-item__stock-message"}
        (when-let [{:keys [message]} (:stock_message variation)]
          [:span
           {:class
            (case (:type message)
              "in-stock" "text-status text-status--success"
              "available-presale" "text-status text-status--info"
              "available-stock" "text-status text-status--info"
              "out-of-stock" "text-status text-status--important"
              "text-status text-status--info")}
           (:message message)
           (when (:dropship message)
             [:a {:href "/help.htm?action=article&id=189"
                  :class (str "text-link text-link--inherit"
                           (when (= (:type message) "out-of-stock") "text-link--negative"))
                  :target "_blank"}
              " Learn more."])
           (when (and (:description message) (not (:dropship message)))
             (:description message))])])

     ;; Why this line greys out checkout (model/line-checkout-issue)
     (when checkout-issue
       [:p {:class "cart-item__stock-message"}
        [:span {:class "text-status text-status--important"}
         (case (:type checkout-issue)
           :reduce
           (let [reduce-by (min (:excess checkout-issue) (:qty item))
                 target-qty (- (:qty item) reduce-by)]
             [:<>
              (str "Only " (:available checkout-issue) " left. Please reduce the quantity to check out. ")
              [:a
               {:href "#"
                :class "text-link text-link--inherit text-link--negative"
                :on-click
                (fn [e]
                  #?(:cljs (.preventDefault e))
                  (model/adjust-qty app-atom item (- reduce-by)))}
               (if (pos? target-qty)
                 (str "Reduce to " target-qty)
                 "Remove")]])

           "Not available to order right now. Please remove this item to check out.")]])

     ;; Special status messages
     (when (and (:comments special) (not (:added special)))
       [:p {:class "cart-item__special-message"}
        [:span {:class "text-status text-status--important"}
         (str/strip-tags (:comments special))]])

     ;; Cart item expiry countdown (carttimeout)
     (when (:expires_at item)
       (cart.common/expiry-countdown (:expires_at item)))

     ;; Time-limited special offer warning (for specials)
     (when (:timeleft item)
       [:p {:class "cart-item__time-message"}
        [:span {:class "text-status text-status--important"}
         [:b (str "You have " (:timeleft item) " minutes left")]
         " to check out this item."]])

     ;; Cover voucher delivery info
     (when (and cover-voucher (not (:forregistry_uuid item)))
       [:p {:class "cart-item__stock-message"}
        [:span
         [:b "Delivery by email"]
         (when (u/presence (:email cover-voucher))
           (str " to " (:email cover-voucher) " — "))
         [:a
          {:href "#"
           :class "text-link"
           :on-click
           (fn [e]
             #?(:cljs
                (do
                  (.preventDefault e)
                  (urlstate/redirect app-atom "gift-vouchers" "editvoucher"
                    {:method (:method cover-voucher)
                     :cover-refid (:cover_refid cover-voucher)
                     :cartitem_uuid (first (:cartitem_uuids item))}))))}
          "Change"]]])]))

(rum/defc quantity-input-combo < rum/reactive [app-atom cart-atom item variation]
  (let [item-id (last (:cartitem_uuids item))
        item-qty (:qty item)
        pending-key [(get-in item [:special :groupnumber]) (:variation_uuid item)]
        pending-entry (get (rum/react model/qty-pending) pending-key)
        pending-delta (or (:delta pending-entry) 0)
        loading? (:loading? pending-entry)
        display-qty (max 0 (+ item-qty pending-delta))]
    [:div {:class "input-combo input-combo--quantity"}
     (when (and (can-reduce-qty? item variation) (not (:qtyfixed item)))
       [:a
        {:href "#"
         :class "input-combo__button button button--raised button--default button--secondary button--bordered button--has-icon-only js-cart-remove-item-button"
         :on-click
         (fn [e]
           #?(:cljs (.preventDefault e))
           (when-not loading?
             (model/adjust-qty app-atom item -1)))}
        [:span {:class "button__text"}
         [:i {:class "button__icon icon icon--inline icon--default icon--is-dark icon--minus"}
          [:span {:class "u-hidden-from-view"} "Remove 1"]]]])

     [:input
      {:type "text"
       :id (str "js-cart-item-input-" item-id)
       :class "text-input"
       :size "3"
       :value display-qty
       :readOnly true
       :on-change #()}]

     (when (and (can-increase-qty? item variation) (not (:qtyfixed item)))
       [:a
        {:href "#"
         :class "input-combo__button button button--raised button--default button--secondary button--bordered button--has-icon-only js-cart-add-item-button"
         :on-click
         (fn [e]
           #?(:cljs (.preventDefault e))
           (when-not loading?
             (model/adjust-qty app-atom item 1)))}
        [:span {:class "button__text"}
         [:i {:class "button__icon icon icon--inline icon--default icon--is-dark icon--plus"}
          [:span {:class "u-hidden-from-view"} "Add 1"]]]])]))

(rum/defc cart-item-options [app-atom cart-atom item variation]
  [:ul {:class "cart-item-options"}
   ;; Move to wishlist button
   (when-not (:system_item item)
     [:li {:class "cart-item-options__item"}
      [:a
       {:href "#"
        :class "cart-item-options__link icon-link icon-block-link js-open-wishlist-item-modal-button"
        :on-click
        (fn [e]
          #?(:cljs (.preventDefault e))
          (grid/confirm-modal app-atom
            {:title "Move to wish list?"
             :text "Are you sure you want to move this item from your cart to your wish list? There is no way to undo this action."
             :confirm-text "Yes, move it"
             :confirm-key :cart/move-to-wishlist
             :confirm-data
             {:cartitem_uuids (:cartitem_uuids item)
              :variation_uuid (:variation_uuid item)
              :groupnumber (get-in item [:special :groupnumber])}}))}
       [:i {:class "icon icon--inline icon--is-dark icon--heart"}]
       [:span {:class "u-hidden-from-view"} "Move to wish list"]]])

   ;; Remove item button
   (when (can-remove? item variation)
     [:li {:class "cart-item-options__item"}
      [:a {:href "#"
           :class "cart-item-options__link icon-link icon-block-link js-open-delete-item-modal-button"
           :on-click
           (fn [e]
             #?(:cljs (.preventDefault e))
             (grid/confirm-modal app-atom
               {:title "Remove Item"
                :text "Are you sure you want to remove this item from your Cart?"
                :negative true
                :confirm-text "Remove"
                :confirm-key :cart/remove-item
                :confirm-data
                (cond->
                  {:cartitem_uuids (:cartitem_uuids item)
                   :variation_uuid (:variation_uuid item)}
                  (get-in item [:special :groupnumber])
                  (assoc :groupnumber (get-in item [:special :groupnumber])))}))}
       [:i {:class "icon icon--inline icon--is-dark icon--delete"}]
       [:span {:class "u-hidden-from-view"} "Remove"]]])])

(rum/defc cart-item-nav [app-atom cart-summary-atom item variation]
  [:nav {:class "cart-item__nav"}
   [:div {:class "cart-item__qty"}
    (if (or (:qtyfixed item) (:system_item item))
      ;; Fixed quantity items (no controls)
      [:div {:class "cart-item__qty-fixed"}
       [:span {:class "cart-item__qty-text"} "Qty: " (:qty item)]]
      ;; Regular quantity controls
      (quantity-input-combo app-atom cart-summary-atom item variation))]
   (cart-item-options app-atom cart-summary-atom item variation)])

(rum/defc cart-item-footer [app-atom cart-summary-atom item variation]
  (let [item-qty (:qty item)
        subtotal (* item-qty (:price item))]
    [:footer {:class "cart-item__footer"}
     [:p {:class "cart-item__subtotal"}
      [:span {:class "clip-text--l"} "Subtotal: "]
      "R" (u/currency-format subtotal)]
     (cart-item-nav app-atom cart-summary-atom item variation)]))

(defn- items-loading? [pending items]
  (some
    (fn [item]
      (:loading?
       (get pending [(get-in item [:special :groupnumber]) (:variation_uuid item)])))
    items))

(rum/defc qty-loading-overlay []
  [:div
   {:style
    {:position "absolute" :top 0 :left 0 :right 0 :bottom 0
     :background "rgba(255,255,255,0.7)" :z-index 10
     :display "flex" :align-items "center" :justify-content "center"}}
   [:i {:class "icon icon--block icon--large icon--is-dark icon--loading u-animate-spin"}]])

(rum/defc free-item-row < rum/reactive
  [app-atom cart-summary-atom item variation]
  (let [specials (cmn/curact cart-summary-atom [:specials])
        variations (cmn/curact cart-summary-atom [:variations])
        special (get specials (get-in item [:special :refid]))
        qty (:qty item)

        free-with-title
        (:title (get variations (:free_with_variation_uuid item)))

        note
        (if free-with-title
          (str "Free with " free-with-title)
          (or
            ;; Special comments can carry HTML authored for the product page
            (some-> (get-in item [:special :comments]) str/strip-tags u/presence)
            (u/presence (:special_applied_message special))))]
    [:article
     {:class "card card--no-hover cart-item"
      :id (str "item" (:id item))}
     [:div {:class "cart-item__content"}
      [:figure {:class "cart-item__hero"}
       [:a {:href (:page_url variation) :class "block-link"}
        (if (:public_id (:image variation))
          (web-common/cloudinary-lazy-image app-atom
            {:public_id (get-in variation [:image :public_id])
             :width 256
             :height 256
             :alt (:title variation)
             :html-class "yc-simple-image yc-simple-image--full cart-item__image"
             :lazyload true})
          [:div {:class "cart-item__no-image"}
           [:div {:class "cart-item__no-image-placeholder"} "No image"]])]]
      [:div {:class "cart-item__description"}
       [:h2 {:class "cart-item__title"}
        [:a {:href (:page_url variation)}
         [:span (:title variation)]]]
       [:small {:class "cart-item__meta"}
        (str "Product code: " (:code variation))]
       (when note
         [:p {:class "cart-item__stock-message"}
          [:span {:class "text-status text-status--important"} note]])
       (if (:gift_unavailable item)
         [:p {:class "cart-item__stock-message"}
          [:span {:class "text-status text-status--important"}
           "Your free gift is no longer available"]]
         (when-let [{:keys [text style]} (:gift_stock_line variation)]
           [:p {:class "cart-item__stock-message"}
            [:span
             {:class
              (case style
                "success" "text-status text-status--success"
                "important" "text-status text-status--important"
                "text-status text-status--info")}
             text]]))]]
     [:footer {:class "cart-item__footer"}
      [:p {:class "cart-item__subtotal"}
       [:span {:class "u-text-red"} "Free"]]
      [:nav {:class "cart-item__nav"}
       [:div {:class "cart-item__qty"}
        [:div {:class "input-combo input-combo--quantity"}
         [:input
          {:type "text"
           :class "text-input"
           :size "3"
           :value (if (:gift_unavailable item) "0" (str (or qty 1)))
           :read-only true}]]]]]]))

(rum/defc cart-item-card < rum/reactive [app-atom cart-summary-atom item]
  (let [variation (cmn/curact cart-summary-atom [:variations (:variation_uuid item)])
        covers (cmn/curact cart-summary-atom [:covers])
        in-bundle? (get-in item [:special :groupnumber])
        qty-pending (rum/react model/qty-pending)

        loading?
        (when-not in-bundle?
          (items-loading? qty-pending [item]))

        ;; Hidden while a qty change for this line is in flight, so "Reduce to"
        ;; can't be applied twice before the summary catches up
        checkout-issue
        (when-not (contains? qty-pending [in-bundle? (:variation_uuid item)])
          (model/line-checkout-issue (rum/react cart-summary-atom) item))]
    (if (model/free-special-item? item)
      (free-item-row app-atom cart-summary-atom item variation)
      [:article
       {:class "card card--no-hover cart-item"
        :id (str "item" (:id item))
        :style (when loading? {:position "relative"})}
       [:div {:class "cart-item__content"}
        (cart-item-hero app-atom cart-summary-atom item variation covers)
        (cart-item-description app-atom cart-summary-atom item variation checkout-issue)]
       (cart-item-footer app-atom cart-summary-atom item variation)
       (when loading? (qty-loading-overlay))])))

(rum/defc bundle-header [groupnumber]
  [:h2 {:class "cart-list-heading"}
   (str "Special bundle " groupnumber ":")])

(rum/defc other-items-header []
  [:h2 {:class "cart-list-heading"}
   "Other items:"])

(rum/defc bundle-discount-line [app-atom cart-summary-atom bundle-group]
  (let [bundle-discounts
        (filter
          (fn [item]
            (= (get-in item [:special :special]) "bundleup"))
          bundle-group)]
    (when (seq bundle-discounts)
      (for [discount bundle-discounts]
        (let [[heading footer] (str/split (get-in discount [:special :comments]) #" - ")]
          [:article {:class "card card--no-hover cart-item cart-item--is-discount"
                     :key (str "bundle-discount-" (:payment_uuid discount))}
           [:div {:class "cart-item__content"}
            [:figure {:class "cart-item__hero"}
           ;; TODO: This should use the cloudinary function
             [:img
              {:src "https://res.cloudinary.com/yuppiechef/image/upload/bo_24px_solid_rgb:fff,c_lpad,f_auto,h_256,q_auto,w_256,b_white/v1/contentdocs/1155/otherpicture320200513101144"
               :alt ""
               :class "yc-simple-image yc-simple-image--full cart-item__image"}]]
            [:div {:class "cart-item__description"}
             [:h2 {:class "cart-item__title u-text-red"}
              heading]
             [:p {:class "cart-item__stock-message"}
              [:span {:class "text-status text-status--important"}
               (str/capitalize footer)]]]]
           [:footer {:class "cart-item__footer"}
            [:p {:class "cart-item__subtotal"}
             [:span {:class "u-text-red"}
              "-R" (u/currency-format (Math/abs (:price discount)))]]]])))))

(rum/defc bundle-group < rum/reactive [app-atom cart-summary-atom bundle-items groupnumber]
  (let [loading?
        (or
          (items-loading? (rum/react model/qty-pending) bundle-items)
          (contains? (rum/react model/removing-groups) groupnumber))]
    [:section
     {:class "cart-item-list cart-item-list--is-group"
      :style {:position "relative"}}
     (bundle-header groupnumber)
     (for [item (remove #(= (get-in % [:special :special]) "bundleup") bundle-items)]
       (rum/with-key
         (cart-item-card app-atom cart-summary-atom item)
         (str "cart-item-"
           (str/join "-" (map item [:variation_uuid :price])))))
     (bundle-discount-line app-atom cart-summary-atom bundle-items)
     (when loading? (qty-loading-overlay))]))

(rum/defc other-items-group [app-atom cart-summary-atom has-bundles? other-items]
  [:section {:class "cart-item-list cart-item-list--is-group"}
   (when has-bundles?
     (other-items-header))
   (for [item other-items]
     (rum/with-key
       (cart-item-card app-atom cart-summary-atom item)
       (str "cart-item-" (str/join "-" (map item [:variation_uuid :price])))))])

(rum/defc donation-callout []
  [:section {:class "cart-item-list"}
   [:a {:href "/donation.htm?ref=desktopcart"
        :class "card text-card text-card--standard u-text-center block-link"}
    [:span {:class "u-text-32"} "🍚🍏🌽🍞"]
    [:p {:class "yc-paragraph yc-paragraph--flush"}
     "Help us feed hungry families — R75 can provide a child's breakfast for a month."]
    [:div {:class "u-margin-top-16"}
     [:span {:class "button button--default button--flat button--primary"}
      "Donate now"]]]])

(rum/defc cart-item-list < rum/reactive cmn/cursors [app-atom cart-summary-atom]
  (let [items (cmn/curact cart-summary-atom [:items])
        variations (cmn/curact cart-summary-atom [:variations])
        rolled-up-items (model/roll-up-cart-items items variations)

        ;; Group items by bundle number
        grouped-items (group-by :groupnumber rolled-up-items)

        ;; Separate bundle items from regular items
        bundle-groups (filter #(> (or (first %) 0) 0) grouped-items)
        regular-items (get grouped-items nil [])

        ;; Check if we have any bundles
        has-bundles? (seq bundle-groups)
        has-other-items? (seq regular-items)]

    (list
      (for [[groupnumber bundle-items] bundle-groups]
        (rum/with-key
          (bundle-group app-atom cart-summary-atom bundle-items groupnumber)
          (str "bundle-group-" groupnumber)))
      (when has-other-items?
        (rum/with-key
          (other-items-group app-atom cart-summary-atom has-bundles? regular-items)
          "other-items")))))

(rum/defc shipping-notification < rum/reactive cmn/cursors [cart-summary-atom]
  "Shipping notification section using centralized cart API dispatch messaging"
  (let [dispatch_message (cmn/curact cart-summary-atom [:dispatch_message])]
    [:div {:class "yc-section yc-section--medium"}
     (feedback/feedback
       (keyword (str/lower-case (:level dispatch_message)))
       (:title dispatch_message)
       [:span {:dangerouslySetInnerHTML {:__html (:message dispatch_message)}}])]))

(rum/defc cart-items-step < rum/reactive cmn/cursors [app-atom process-atom cart-summary-atom]
  "Render the cart items step"
  (let [message (cmn/curact cart-summary-atom [:message])

        stock-blocked?
        (boolean
          (seq (model/stock-blocked-validations (rum/react cart-summary-atom))))]
    [:<>
       ;; Shipping notification section
     (shipping-notification cart-summary-atom)

     (when-not (str/blank? message)
       (feedback/feedback "info" message))

     (cart.common/checkout-layout process-atom true
       (cart.common/main-cart-content-wrapper app-atom cart-summary-atom
         (list
           (cart.common/cart-login-prompt cart-summary-atom)
           (rum/with-key (cart.discounts/callouts-section app-atom cart-summary-atom) "callouts")
           (rum/with-key (cart-item-list app-atom cart-summary-atom) "cart-items")
           (rum/with-key (cart.discounts/discounts-section app-atom cart-summary-atom) "discounts")
           (when-not (some :forregistry_uuid (cmn/curact cart-summary-atom [:items]))
             (rum/with-key (donation-callout) "donation"))
           (cart.common/order-calculations app-atom cart-summary-atom)))

       (cart.common/checkout-cta-sidebar app-atom process-atom cart-summary-atom
         {:disabled? stock-blocked?}))

     [:div {:class "clearer"}]]))

(defmethod cart.common/checkout-step :cart/items
  [app-atom process-atom cart-summary-atom]
  (cart-items-step app-atom process-atom cart-summary-atom))